// Shared by the receipt page and the backend's browser-to-PDF renderer.
// React is supplied by the caller so neither app depends on the other's node_modules.
const money = (value) => Number.isFinite(+value) ? `$ ${(+value).toFixed(2)}` : "$ 0.00";
const flag = (value) => ["true", "1", "yes"].includes(String(value ?? "").toLowerCase());

function formatPhone(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function formatEngineSize(value) {
  const raw = String(value || "").trim();
  return !raw ? "-" : /l\b/i.test(raw) ? raw : `${raw} L`;
}

function formatDrivetrain(value) {
  const raw = String(value || "").trim();
  const normalized = raw.toLowerCase();
  if (!raw) return "-";
  if (normalized.includes("four") || normalized.includes("4wd") || normalized.includes("4x4")) return "4WD";
  if (normalized.includes("all") || normalized.includes("awd")) return "AWD";
  if (normalized.includes("front") || normalized.includes("fwd")) return "FWD";
  if (normalized.includes("rear") || normalized.includes("rwd")) return "RWD";
  if (["two", "2wd", "2-wheel", "2 wheel", "4x2"].some((term) => normalized.includes(term))) return "2WD";
  return raw;
}

function getDocumentMetadata(receipt = {}) {
  const isEstimate = String(receipt.receiptType || "Quote").toLowerCase() === "quote";
  const documentType = isEstimate ? "Estimate" : "Receipt";
  const documentNumber = String(receipt.friendlyNumber || receipt.invoiceNumber ||
    (receipt._id ? `INV-${String(receipt._id).slice(-6).toUpperCase()}` : "--"));
  return {
    isEstimate, documentType, documentNumber,
    filename: `${documentType}-${documentNumber.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 80)}.pdf`,
  };
}

function formatDate(value, timeZone) {
  if (!value) return "--";
  // A date-only value is a calendar day, not a UTC timestamp.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
  const date = new Date(dateOnly ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return "--";
  return new Intl.DateTimeFormat("en-US", {
    month: "long", day: "2-digit", year: "numeric",
    ...(dateOnly ? { timeZone: "UTC" } : timeZone ? { timeZone } : {}),
  }).format(date);
}

function safeLogoSrc(value) {
  const src = String(value || "").trim();
  return /^(https?:\/\/|\/|data:image\/(?:png|jpe?g|gif|webp);base64,)/i.test(src) ? src : "";
}

function createReceiptPrintElement(React, { receipt = {}, shop = {}, logoSrc = "", timeZone } = {}) {
  const h = React.createElement;
  const box = (className, ...children) => h("div", { className }, ...children);
  const info = (label, value) => h("div", null, h("strong", null, `${label}:`), " ", value);
  const customer = receipt.customer || {};
  const vehicle = receipt.vehicle || {};
  const { isEstimate, documentNumber } = getDocumentMetadata(receipt);
  const logo = safeLogoSrc(logoSrc);
  const services = (Array.isArray(receipt.services) ? receipt.services : []).filter((row) => {
    const value = row.value || row;
    return !(value.type === "part" && flag(value.includedInGroupService));
  });
  const tires = Array.isArray(receipt.tires) ? receipt.tires : [];
  const subTotal = (Number(receipt.totalPrice || 0) - Number(receipt.tax || 0)).toFixed(2);
  const storedDiscount = Number(receipt.discountAmount);
  const discount = Number.isFinite(storedDiscount) && storedDiscount > 0 ? storedDiscount
    : receipt.discountType === "Percentage"
      ? Number(receipt.subTotal || subTotal || 0) * Number(receipt.discount || 0) / 100
      : Number(receipt.discount || 0);
  const taxVisible = flag(receipt.taxInclude ?? receipt.taxIncluded) || Number(receipt.tax) > 0;
  const locality = [customer.city, customer.state].map((value) => String(value || "").trim()).filter(Boolean).join(", ");
  const address = [String(customer.address || "").trim(), [locality, customer.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const price = (value) => `$${Number(value || 0).toFixed(2)}`;
  const line = (key, description, rate, quantity, total) => h("tr", { key },
    h("td", null, description), h("td", null, price(rate)), h("td", null, quantity), h("td", null, price(total)));
  const total = (label, value, className = "row") => box(className, h("span", null, label), h("span", null, value));

  return box("receipt-print",
    box("invoice-wrap",
      box("invoice-header",
        box("company-info",
          logo ? h("img", { src: logo, alt: "Company Logo", className: "company-logo-print" })
            : shop.name ? h("h2", { className: "company-name" }, shop.name) : null,
          box("company-contact",
            h("div", { style: { whiteSpace: "pre-line" } }, String(shop.address || "").replace(", ", ",\n")),
            h("div", null, shop.phone))),
        box("invoice-title", isEstimate ? "ESTIMATE" : "INVOICE")),
      box("invoice-meta", h("div", null, info("Date", formatDate(receipt.date, timeZone))),
        h("div", null, info(isEstimate ? "Estimate #" : "Invoice #", documentNumber))),
      box("info-sections",
        box("info-box", box("section-title", "Customer Info"),
          info("Name", customer.name || "-"), info("Address", address || "-"),
          info("Phone", formatPhone(customer.phone)), info("Email", customer.email || "-")),
        box("info-box", box("section-title", "Vehicle Info"),
          info("Make", vehicle.make || "-"), info("Model", vehicle.model || "-"), info("Year", vehicle.year || "-"),
          info("VIN", vehicle.vin_number || "-"),
          Number.isFinite(+receipt.miles) ? info("Mileage", `${(+receipt.miles).toLocaleString("en-US")} mi`) : null,
          info("Engine Size", formatEngineSize(vehicle.engine_size)), info("Cylinders", vehicle.engine_cylinders || "-"),
          info("Drivetrain", formatDrivetrain(vehicle.drivetrain)))),
      box("table-section", h("table", { className: "invoice-table" },
        h("thead", null, h("tr", null, h("th", { style: { width: "50%" } }, "Description"),
          h("th", null, "Rate"), h("th", null, "Qty"), h("th", null, "Price"))),
        h("tbody", null,
          ...services.map((row, index) => {
            const value = row.value || row;
            return line(`service-${index}`, h(React.Fragment, null,
              h("strong", null, value.name || row.label), value.description ? box("desc", value.description) : null),
            value.price, Number(value.quantity || 1), value.total_price);
          }),
          ...tires.map((tire, index) => {
            const type = tire.condition || tire.quality || "";
            return line(`tire-${index}`, h(React.Fragment, null,
              h("strong", null, `${type}${type ? " - " : ""} ${tire.brand || ""} ${tire.size || ""}`),
              tire.description ? box("desc", tire.description) : null,
              tire.type === "tire_service" ? h("div", { className: "desc", style: { fontSize: "9px", fontStyle: "italic" } }, "(Includes 2% Disposal Fee)") : null),
            tire.price, Number(tire.quantity || 1), tire.total_price);
          }),
          receipt.oil ? line("oil", h("strong", null, `Oil: ${receipt.oil.name || ""} (${receipt.oil.brand || ""})`), receipt.oilPrice, 1, receipt.oilPrice) : null,
          Number(receipt.extraOilQuantity) > 0 ? line("extra-oil",
            h("strong", null, `Extra Oil: ${receipt.extraOilQuantity} qt @ ${price((receipt.extraOilPrice || 0) / receipt.extraOilQuantity)} each`),
            (receipt.extraOilPrice || 0) / receipt.extraOilQuantity, receipt.extraOilQuantity, receipt.extraOilPrice) : null,
          receipt.oilFilter ? line("filter", h("strong", null,
            `Filter: ${receipt.oilFilter.name || ""}${receipt.oilFilter.type ? ` (${receipt.oilFilter.type})` : ""}`),
          receipt.oilFilterPrice, Number(receipt.oilFilterQuantity || 1), receipt.oilFilterPrice) : null,
          flag(receipt.fullServiceIncluded) ? line("full-service", h(React.Fragment, null,
            h("strong", null, "Full Service Included"),
            box("desc", "TOP OFF ALL FLUIDS - COOLANT, WINDSHIELD WASHER, POWER STEERING FLUID, BRAKE FLUID AND CHECK TIRES PRESSURE")),
          receipt.fullServicePrice || 3.99, 1, receipt.fullServicePrice || 3.99) : null))),
      box("tech-notes-section", box("tech-notes-label", "Tech Notes:"),
        receipt.techNotes || receipt.tech_notes ? box("tech-notes-box", receipt.techNotes || receipt.tech_notes) : null),
      box("totals-box",
        total("Subtotal:", money(subTotal)),
        String(receipt.discountInclude) === "true" ? total("Discount:",
          `- ${money(discount)}${String(receipt.discountType).toLowerCase() === "percentage" ? ` (${receipt.discount}%)` : ""}`) : null,
        taxVisible ? total("Tax:", money(receipt.tax)) : null,
        total("TOTAL:", money(receipt.totalPrice), "row total"), total("Paid:", money(receipt.paid)), total("Remaining:", money(receipt.remaining)),
        Number(receipt.overpaymentAmount) > 0 ? total(receipt.overpaymentDisposition === "customer_credit" ? "Customer credit:" : "Refund due:", money(receipt.overpaymentAmount)) : null),
      box("invoice-footer", "Thank you for your business!")));
}

export { createReceiptPrintElement, getDocumentMetadata, formatDate, formatPhone, formatEngineSize, formatDrivetrain };
